import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import { Estabelecimento, Usuario, TipoUsuario } from '../types';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../utils/errorHandler';

// ===== Estabelecimentos =====

export const useListarEstabelecimentos = () => {
  return useQuery({
    queryKey: ['estabelecimentos'],
    queryFn: async () => {
      const response = await api.get<Estabelecimento[]>('/estabelecimentos');
      return response.data;
    },
  });
};

export const useObterEstabelecimento = (id: number | undefined) => {
  return useQuery<Estabelecimento>({
    queryKey: ['estabelecimento', id],
    queryFn: async () => {
      const response = await api.get<Estabelecimento>(`/estabelecimentos/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCriarEstabelecimento = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { nome: string; endereco: string; telefone: string; quantidadeMesas: number }) => {
      const response = await api.post('/estabelecimentos', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estabelecimentos'] });
      toast.success('Estabelecimento criado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao criar estabelecimento'));
    },
  });
};

export const useAtualizarEstabelecimento = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number; nome: string; endereco: string; telefone: string; quantidadeMesas: number }) => {
      const response = await api.put(`/estabelecimentos/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estabelecimentos'] });
      toast.success('Estabelecimento atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar estabelecimento'));
    },
  });
};

// ===== Usuários =====

export const useListarUsuarios = () => {
  return useQuery({
    queryKey: ['usuarios'],
    queryFn: async () => {
      const response = await api.get<Usuario[]>('/usuarios');
      return response.data;
    },
  });
};

export const useCadastrarUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      nome: string;
      email: string;
      senha: string;
      tipo: TipoUsuario;
      estabelecimentoId: number | null;
      usuarioLogadoId: number;
    }) => {
      const response = await api.post('/usuarios', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Usuário cadastrado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao cadastrar usuário'));
    },
  });
};

export const useAlternarStatusUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.patch(`/usuarios/${id}/alternar-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      queryClient.invalidateQueries({ queryKey: ['funcionarios'] });
      toast.success('Status do usuário alterado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao alterar status do usuário'));
    },
  });
};

export const useListarFuncionarios = (params?: { nome?: string; email?: string; estabelecimentoId?: number }) => {
  return useQuery({
    queryKey: ['funcionarios', params],
    queryFn: async () => {
      const response = await api.get<Usuario[]>('/usuarios/funcionarios', { params });
      return response.data;
    },
  });
};

export const useAtualizarUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number; nome: string; email: string }) => {
      const response = await api.put(`/usuarios/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      queryClient.invalidateQueries({ queryKey: ['funcionarios'] });
      toast.success('Usuário atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar usuário'));
    },
  });
};
